import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/api/path';
import { webSocketService } from '@/services/websocket';
import {
  currentBusinessAtom,
  currentUserAtom,
  isAuthenticatedAtom,
  roleAtom,
} from '@/store/atoms';

export const useLogout = () => {
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const setCurrentBusiness = useSetAtom(currentBusinessAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);
  const setRole = useSetAtom(roleAtom);
  const queryClient = useQueryClient();

  const logoutMutation = useMutation({
    mutationFn: async (): Promise<{ success: boolean }> => {
      const response = await fetch(`${API_BASE_URL}/api/auth/signout`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Logout failed');
      }
      return response.json() as Promise<{ success: boolean }>;
    },
    onSuccess: () => {
      // Clear authentication state
      setIsAuthenticated(false);
      setCurrentBusiness(null);
      setCurrentUser(null);
      setRole(null);

      // Disconnect and clear WebSocket
      webSocketService.disconnect();
      webSocketService.clearCache();
      localStorage.removeItem('ws_v2_last_seq');

      // Clear all React Query cache
      queryClient.clear();

      // Show toast before redirect
      toast.success('Logged out successfully');

      // Hard redirect to login page to avoid route guard issues
      // This ensures we're completely out of the authenticated app routes
      window.location.href = '/login';
    },
    onError: () => {
      toast.error('Failed to logout');
    },
  });

  return {
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
};
